"use client";
import { Fragment, useState, type MouseEventHandler } from "react";
import BFLLink from "@repo/shared-ui/BFLLink";
import Products from "./Products";
import { handleFooterAccordionClickDL, handleFooterLinkClickDL } from "./FooterAccordionFunction";
import Conditional from "@repo/shared-ui/Conditional";

interface AccordionItem {
  title: string;
  content: FooterLinkItem[];
}

interface FooterLinkItem {
  linkTitle?: string;
  linkUrl?: string;
  newTab?: string;
  nodeName?: string;
  children?: FooterLinkItem[];
}

type FooterLinkCollection = FooterLinkItem[] | Record<string, FooterLinkItem> | null | undefined;

interface SocialMediaIconItem {
  titleText?: string;
  socialMediaUrl?: string;
}

interface FooterAccordionProps {
  loanLinks: FooterLinkItem[] | null | undefined;
  reachUsData: FooterLinkCollection;
  calculatorData: FooterLinkCollection;
  importantLink: FooterLinkCollection;
  importantLinkTitle?: string;
  productsData: Record<string, any>[] | null | undefined;
  socialMediaIcons:
    | SocialMediaIconItem[]
    | Record<string, SocialMediaIconItem>
    | null
    | undefined;
}

const getSafeArray = <T,>(value: T[] | Record<string, T> | null | undefined): T[] => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (value && typeof value === "object") {
    return Object.values(value).filter(Boolean);
  }

  return [];
};

const RenderSocialMediaIcons = (type: string, url: string, index: number) => {
  const commonClass = "w-8 h-8 rounded-full p-1 flex-center-center text-sm";
  const ytClass = "bg-yt-red";
  const fbClass = "bg-fb-blue";
  const xClass = "bg-black";
  const lInClass = "bg-lin-blue";

  return (
    <Fragment key={`${type}-${index}`}>
      <Conditional if={type === "Facebook"}>
        <BFLLink href={url} className={`${commonClass} ${fbClass}`}>
          <i className="icomoon before:content-['\e915']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "LinkedIn"}>
        <BFLLink href={url} className={`${commonClass} ${lInClass}`}>
          <i className="icomoon before:content-['\e917']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "Twitter"}>
        <BFLLink href={url} className={`${commonClass} ${xClass}`}>
          <i className="icomoon before:content-['\e95a']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "YouTube"}>
        <BFLLink href={url} className={`${commonClass} ${ytClass}`}>
          <i className="icomoon before:content-['\e920']" />
        </BFLLink>
      </Conditional>
    </Fragment>
  );
};

const FooterAccordion: React.FC<FooterAccordionProps> = ({
  loanLinks,
  reachUsData,
  calculatorData,
  importantLink,
  importantLinkTitle = "Important Links",
  productsData,
  socialMediaIcons,
}) => {
  const safeLoanLinks = getSafeArray(loanLinks);
  const safeProductsData = getSafeArray(productsData);
  const safeCalculatorData = getSafeArray(calculatorData);
  const safeImportantLink = getSafeArray(importantLink);
  const safeReachUsData = getSafeArray(reachUsData);
  const safeSocialMediaIcons = getSafeArray(socialMediaIcons);

  const accordionData: AccordionItem[] = [
    {
      title: "Application forms",
      content: safeLoanLinks,
    },
    {
      title: "Products portfolio",
      content: safeProductsData,
    },
    {
      title: "Calculators",
      content: safeCalculatorData,
    },
    {
      title: importantLinkTitle,
      content: safeImportantLink,
    },
    {
      title: "Reach Us",
      content: safeReachUsData,
    },
  ];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleAccordion = (index: number) => {
    // Track analytics event for accordion click
    const title = accordionData[index]?.title ?? "";
    // Pass loginStatus (empty string), title, and cta_text
    handleFooterAccordionClickDL("", title, title);

    setOpenIndex(openIndex === index ? null : index);
  };

  const handleFooterLinkClick: MouseEventHandler<HTMLElement> = (event) => {
    const { linkTitle, sectionTitle, destinationUrl } = event.currentTarget.dataset;
    handleFooterLinkClickDL(linkTitle ?? "", sectionTitle ?? "", destinationUrl ?? "#");
  };

  const renderApplicationFormLinks = (item: AccordionItem) => {
    const first = item?.content?.[0] as FooterLinkItem | undefined;
    if (!first || !Array.isArray(first.children)) {
      return null;
    }

    return first.children.map((link: FooterLinkItem, idx: number) => {
      const destinationUrl = link.linkUrl ?? "#";
      return (
        <BFLLink
          key={link?.nodeName ?? link?.linkTitle ?? idx}
          href={destinationUrl}
          openInNewTab={link.newTab}
          className="my-1 w-[50%] pr-2 leading-[19.2px] md:!w-1/5 font-[Rubik,Mukta,BalooThambi2,sans-serif]"
          data-link-title={link?.linkTitle ?? ""}
          data-section-title={item.title}
          data-destination-url={destinationUrl}
          onClick={handleFooterLinkClick}
        >
          {link?.linkTitle}
        </BFLLink>
      );
    });
  };

  const renderStandardLinks = (item: AccordionItem) =>
    (item?.content ?? []).map((link, idx) => {
      const destinationUrl = link.linkUrl ?? "#";
      return (
        <BFLLink
          key={link?.nodeName ?? link?.linkTitle ?? idx}
          href={destinationUrl}
          openInNewTab={link.newTab}
          className={`my-1 w-[50%] leading-[19.2px] [font-family:Rubik,Mukta,BalooThambi2,sans-serif] break-words whitespace-normal ${item.title === "Reach Us" ? "pr-0 w-[45%]!" : "pr-0 md:pr-2 md:!w-1/5"}`}
          data-link-title={link?.linkTitle ?? ""}
          data-section-title={item.title}
          data-destination-url={destinationUrl}
          onClick={handleFooterLinkClick}
        >
          {link?.linkTitle}
        </BFLLink>
      );
    });

  return (
    <div className="mx-auto w-full cursor-pointer">
      {accordionData.map((item, index) => (
        (() => {
          const sectionTitle = item?.title ?? "";

          return (
            <div key={"footacc-" + index} className="border-stealblue-100 border-b px-4 py-3">
              <button
                onClick={() => toggleAccordion(index)}
                className="bg-bfl-blue flex w-full cursor-pointer items-center justify-between text-left text-base font-medium capitalize transition [font-family:Rubik-Medium,Mukta-Medium,BalooThambi2-Medium,sans-serif]"
                data-section-title={sectionTitle}
              >
                {sectionTitle}
                <i
                  className={`icomoon transform transition-transform duration-300 before:content-['\\e909'] ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              <div
                className={`bg-bfl-blue transition-all duration-300 ease-in-out ${
                  openIndex === index ? "" : "hidden"
                } ${sectionTitle === "Reach Us" ? "flex flex-col md:flex-row" : "block"}`}
              >
                {sectionTitle === "Products portfolio" ? (
                  <Products data={safeProductsData} />
                ) : (
                  <>
                    <div
                      className={`flex flex-wrap text-sm ${
                        sectionTitle === "Reach Us" ? "w-full lg:!w-[40%]" : "w-full"
                      }`}
                    >
                      {sectionTitle === "Application forms"
                        ? renderApplicationFormLinks(item)
                        : renderStandardLinks(item)}
                    </div>
                    {sectionTitle === "Reach Us" && (
                      <div className="flex gap-4 mt-2">
                        {safeSocialMediaIcons.map(({ titleText, socialMediaUrl }, idx) =>
                          RenderSocialMediaIcons(titleText ?? "", socialMediaUrl ?? "", idx)
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })()
      ))}
    </div>
  );
};
export default FooterAccordion;
